'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import s from './video.module.css';

interface VideoProps {
    controls?: boolean;
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    desktopVideoUrl?: any;
    mobileVideoUrl?: any;
    link?: string;
    poster?: string;
}
export const breakpointMedium = '991px';
const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : () => {};

const useWindowReSize = () => {
    const [size, setSize] = useState([0, 0]);

    useIsomorphicLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);

        updateSize();

        return () => window.removeEventListener('resize', updateSize);
    }, []);

    return size;
};

export const Video = ({
    desktopVideoUrl,
    mobileVideoUrl,
    controls,
    autoplay,
    muted,
    loop,
    link,
    poster,
}: VideoProps) => {
    const [width] = useWindowReSize();
    const [videoLayout, setVideoLayout] = useState('small');
    const desktopVideoRef = useRef<HTMLVideoElement>(null);
    const mobileVideoRef = useRef<HTMLVideoElement>(null);
    const breakpointAsInteger = (string: string) =>
        parseInt(string.replace('px', ''));

    useIsomorphicLayoutEffect(() => {
        if (width >= breakpointAsInteger(breakpointMedium)) {
            setVideoLayout('medium');
        } else {
            setVideoLayout('small');
        }
    }, [width]);

    useIsomorphicLayoutEffect(() => {
        if (!autoplay) return;
        if (videoLayout === 'medium' && desktopVideoRef.current) {
            desktopVideoRef.current.play().catch(() => {});
        } else if (videoLayout === 'small' && mobileVideoRef.current) {
            mobileVideoRef.current.play().catch(() => {});
        }
    }, [videoLayout, autoplay]);

    const videoMarkUp = (
        <>
            <div className={s.desktopVideo}>
                <video
                    ref={desktopVideoRef}
                    controls={controls}
                    muted={muted}
                    playsInline
                    width="100%"
                    height="100%"
                    loop={loop}
                    preload="auto"
                    className={s.video}
                    poster={poster}
                >
                    <source src={desktopVideoUrl} type="video/mp4" />
                </video>
            </div>
            <div className={s.mobileVideo}>
                <video
                    ref={mobileVideoRef}
                    controls={controls}
                    muted={muted}
                    playsInline
                    width="100%"
                    height="auto"
                    loop={loop}
                    preload="auto"
                    className={s.video}
                    poster={poster}
                >
                    <source src={mobileVideoUrl} type="video/mp4" />
                </video>
            </div>
        </>
    );

    if (link && link.length) {
        return <a href={link}>{videoMarkUp}</a>;
    }

    return videoMarkUp;
};

Video.displayName = 'Video';
export default Video;
